import type { App } from 'vue';
import type { Message } from '../../types/message';
import { buildApiUrl } from '../../utils/url';
import { WebSocketClient } from '../../utils/websocket';
import { BaseService } from '../base';

// WebSocket 初始化配置（用于应用初始化）
export interface WebSocketConfig {
  // WebSocket 基础 URL
  baseURL: string;
  // 是否自动连接（默认 false）
  autoConnect?: boolean;
  // 获取用户认证信息的函数（用于自动连接）
  getAuthInfo?: () => { userId: number; key: string } | null;
  // 消息处理器（可选，也可以通过 subscribe 订阅）
  onMessage?: (msg: Message) => void;
}

// WebSocket 连接配置（用于手动连接）
export interface WebSocketServiceConfig {
  // WebSocket 基础 URL
  wsUrl: string;
  // 用户 ID
  userId: number;
  // 认证 key
  authKey: string;
}

// 消息订阅回调类型
export type MessageCallback = (msg: Message) => void;

// WebSocket 服务类
export class WebSocketService extends BaseService {
  private ws: WebSocketClient | null = null;
  private subscribers: MessageCallback[] = [];
  private initConfig: WebSocketConfig | null = null;

  constructor(initConfig?: WebSocketConfig) {
    super('WebSocketService');
    this.initConfig = initConfig || null;
  }

  // 初始化服务
  protected async onInitialize(_app?: App): Promise<void> {
    // SSR 环境下不初始化 WebSocket 连接
    if (import.meta.server) return;

    // 如果配置了消息处理器，自动订阅
    if (this.initConfig?.onMessage) {
      this.subscribe(this.initConfig.onMessage);
    }

    // 如果配置了自动连接，首次尝试连接
    if (this.initConfig?.autoConnect) {
      this.autoReconnect();
    }
  }

  // 自动重连（根据 getAuthInfo 返回值决定连接或断开）
  public autoReconnect(): void {
    // SSR 环境下不执行
    if (import.meta.server) return;
    if (!this.initConfig?.autoConnect || !this.initConfig.baseURL) return;

    const authInfo = this.initConfig.getAuthInfo?.();
    if (authInfo) {
      // 有 token，连接或重连
      this.disconnect();
      this.connect({
        wsUrl: this.initConfig.baseURL,
        userId: authInfo.userId,
        authKey: authInfo.key,
      });
    } else {
      // 无 token，断开连接
      this.disconnect();
    }
  }

  // 连接 WebSocket
  public connect(config: WebSocketServiceConfig, forceConnect = false): void {
    // SSR 环境下不执行
    if (import.meta.server) return;

    if (this.ws && !forceConnect) {
      // WebSocket 已连接
      return;
    }

    // 如果强制重连，先关闭旧连接
    if (forceConnect && this.ws) {
      this.ws.destroy();
      this.ws = null;
    }

    // 构建 WebSocket URL
    const wsUrl = buildApiUrl(config.wsUrl, `/ws/message_center/${config.userId}`, {
      key: config.authKey,
    });

    // 创建 WebSocket 连接
    this.ws = new WebSocketClient(wsUrl);

    // 设置消息处理
    this.ws.onMessage(msg => {
      // 通知所有订阅者
      this.subscribers.forEach(callback => {
        try {
          callback(msg as unknown as Message);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[WebSocketService] 订阅者处理消息时出错:', error);
        }
      });
    });
  }

  // 订阅消息
  public subscribe(callback: MessageCallback): () => void {
    if (!this.subscribers.includes(callback)) {
      this.subscribers.push(callback);
    }

    // 返回取消订阅函数
    return () => this.unsubscribe(callback);
  }

  // 取消订阅
  public unsubscribe(callback: MessageCallback): void {
    this.subscribers = this.subscribers.filter(sub => sub !== callback);
  }

  // 发送消息
  public send(message: string | Record<string, unknown>): void {
    if (!this.ws) {
      // WebSocket 未连接
      return;
    }
    this.ws.sendMessage(message);
  }

  // 断开连接
  public disconnect(): void {
    if (this.ws) {
      this.ws.destroy();
      this.ws = null;
    }
  }

  // 销毁服务
  protected async onDestroy(): Promise<void> {
    if (this.ws) {
      this.ws.destroy();
      this.ws = null;
    }
    this.subscribers = [];
    // @ts-expect-error - 清理 initConfig 引用
    this.initConfig = null;
  }
}

// 全局 WebSocket 服务实例
let globalWebSocketService: WebSocketService | null = null;

// 初始化 WebSocket 服务
export const initWebSocketService = async (config?: WebSocketConfig): Promise<WebSocketService> => {
  // SSR 环境下不初始化 WebSocket 服务（WebSocket 服务仅在客户端使用）
  if (import.meta.server) {
    throw new Error('WebSocketService should not be initialized in SSR environment');
  }

  if (!globalWebSocketService) {
    globalWebSocketService = new WebSocketService(config);
    await globalWebSocketService.initialize();
  }
  return globalWebSocketService;
};

// 获取 WebSocket 服务实例
export const getWebSocketService = (): WebSocketService => {
  if (!globalWebSocketService) {
    throw new Error('WebSocketService not initialized. Call initWebSocketService first.');
  }
  return globalWebSocketService;
};

// 便捷的 Hook 函数（用于组件中订阅消息）
export function useWebSocket(callback: MessageCallback) {
  // SSR 环境下返回空实现
  if (import.meta.server) {
    return {
      send: () => {},
      unsubscribe: () => {},
    };
  }

  const service = getWebSocketService();

  // 订阅消息
  const unsubscribe = service.subscribe(callback);

  return {
    // 发送消息
    send: (message: string | Record<string, unknown>) => service.send(message),
    // 取消订阅
    unsubscribe,
  };
}
